import React from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LoadingButton from '@mui/lab/LoadingButton';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { QRCodeCanvas } from 'qrcode.react';

import {
  FINAL_STATUSES,
  NOT_SENDABLE_STATUSES,
  typeLabel,
} from '../constants/paymentLinks.constants';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  getQrValue,
  getStatusConfig,
  getStatusDescription,
  parseDate,
} from '../utils/paymentLinks.utils';
import { StatusChip } from './StatusChip.component';

interface LinkDetailDialogProps {
  open: boolean;
  detail: any;
  detailLoading: boolean;
  refreshingId: string;
  fullScreen: boolean;
  onClose: () => void;
  onCopy: (link: string) => void;
  onOpenCheckout: (link: string) => void;
  onRefresh: (item: any) => void;
  onSend: (item: any) => void;
  onDownloadSummary: (item: any) => void;
  onDownloadQr: (item: any) => void;
}

const visible = (value: any) =>
  value !== undefined && value !== null && value !== '';

const pickLastActivity = (detail: any) =>
  detail.paid_at ||
  detail.rejected_at ||
  detail.sent_at ||
  detail.opened_at ||
  detail.last_checked_at ||
  detail.created_at;

const hasAnyValue = (detail: any, keys: string[]) =>
  keys.some((key) => visible(detail?.[key]));

const finalStatuses = ['paid', 'rejected', 'expired', 'invalid', 'error'];

const getDisplayLinkId = (detail: any) =>
  detail?.reference_id ||
  detail?.external_reference ||
  detail?.id ||
  detail?.short_code ||
  '';

const getRecommendation = (detail: any) => {
  const status = detail?.status;

  if (status === 'paid') {
    return {
      title: 'Cobrança concluída',
      description: 'Pagamento confirmado. Baixe o resumo para conferência.',
      action: 'download',
      actionLabel: 'Baixar resumo',
    };
  }

  if (status === 'expired') {
    return {
      title: 'Link expirado',
      description: 'Este link venceu. Use o resumo para conferir os dados.',
      action: 'download',
      actionLabel: 'Baixar resumo',
    };
  }

  if (['rejected', 'invalid'].includes(status)) {
    return {
      title: 'Atenção necessária',
      description: 'A cobrança não foi concluída. Confira o resumo do link.',
      action: 'download',
      actionLabel: 'Baixar resumo',
    };
  }

  if (status === 'error') {
    return {
      title: 'Falha na consulta',
      description:
        'Atualize a situação para tentar obter o estado mais recente.',
      action: 'refresh',
      actionLabel: 'Atualizar situação',
    };
  }

  if (status === 'created') {
    return {
      title: 'Próxima ação recomendada',
      description: 'Envie o link para o cliente iniciar o pagamento.',
      action: 'send',
      actionLabel: 'Enviar link',
    };
  }

  if (
    [
      'checkout_started',
      'payment_submitted',
      'analyzing',
      'processing',
    ].includes(status)
  ) {
    return {
      title: 'Pagamento em andamento',
      description: 'Atualize a situação para acompanhar a confirmação.',
      action: 'refresh',
      actionLabel: 'Atualizar situação',
    };
  }

  return {
    title: 'Próxima ação recomendada',
    description: 'Envie um lembrete amigável para incentivar o pagamento.',
    action: 'send',
    actionLabel: 'Enviar lembrete',
  };
};

const milestoneDefinitions = [
  {
    key: 'created',
    label: 'Criado',
    color: 'info.main',
    statuses: ['created'],
    eventTypes: ['created'],
    dateFields: ['created_at'],
  },
  {
    key: 'sent',
    label: 'Enviado',
    color: 'secondary.main',
    statuses: [],
    eventTypes: ['sent', 'dispatch_failed'],
    dateFields: ['sent_at'],
  },
  {
    key: 'opened',
    label: 'Aberto',
    color: 'primary.main',
    statuses: ['open'],
    eventTypes: ['open', 'opened'],
    dateFields: ['opened_at'],
  },
  {
    key: 'identified',
    label: 'Identificado',
    color: 'success.main',
    statuses: ['identified'],
    eventTypes: ['identified'],
    dateFields: [],
  },
  {
    key: 'payment',
    label: 'Pagamento',
    color: 'warning.main',
    statuses: [
      'checkout_started',
      'payment_submitted',
      'analyzing',
      'processing',
    ],
    eventTypes: [
      'checkout_started',
      'payment_submitted',
      'analyzing',
      'processing',
    ],
    dateFields: [],
  },
  {
    key: 'result',
    label: 'Resultado',
    color: 'success.main',
    statuses: finalStatuses,
    eventTypes: finalStatuses,
    dateFields: ['paid_at', 'rejected_at'],
  },
];

const getEventTime = (event: any) =>
  parseDate(event?.created_at)?.getTime() || 0;

const sortEventsByDate = (events: any[]) =>
  events
    .slice()
    .sort((left, right) => getEventTime(left) - getEventTime(right));

const matchesMilestone = (event: any, milestone: any) =>
  milestone.eventTypes.includes(event?.event_type) ||
  milestone.statuses.includes(event?.status);

const getMilestoneFieldDate = (detail: any, milestone: any) =>
  milestone.dateFields
    .map((field: string) => detail?.[field])
    .find((value: string) => visible(value));

const getResultMilestoneLabel = (detail: any, event?: any) => {
  const status = finalStatuses.includes(detail?.status)
    ? detail.status
    : event?.status;
  if (!status || !finalStatuses.includes(status)) return 'Resultado';
  return getStatusConfig(status).label;
};

const getResultMilestoneColor = (detail: any, event?: any) => {
  const status = finalStatuses.includes(detail?.status)
    ? detail.status
    : event?.status;
  if (status === 'paid') return 'success.main';
  if (['rejected', 'invalid', 'error'].includes(status)) return 'error.main';
  if (status === 'expired') return 'text.secondary';
  return 'divider';
};

const buildMilestones = (detail: any, events: any[]) => {
  const sortedEvents = sortEventsByDate(events);

  return milestoneDefinitions.map((definition) => {
    const matchingEvents = sortedEvents.filter((event) =>
      matchesMilestone(event, definition)
    );
    const firstEvent = matchingEvents[0];
    const reachedAt =
      firstEvent?.created_at || getMilestoneFieldDate(detail, definition);
    const active =
      definition.statuses.includes(detail?.status) ||
      (definition.key === 'result' && finalStatuses.includes(detail?.status));

    return {
      ...definition,
      count: matchingEvents.length,
      color:
        definition.key === 'result'
          ? getResultMilestoneColor(detail, firstEvent)
          : definition.color,
      label:
        definition.key === 'result'
          ? getResultMilestoneLabel(detail, firstEvent)
          : definition.label,
      reachedAt,
      status: firstEvent?.status,
      active,
    };
  });
};

const SummaryMetric = ({
  label,
  value,
  icon,
  featured,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  featured?: boolean;
}) => (
  <Box minWidth={0}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Box display="flex" alignItems="center" gap={0.75} minWidth={0} mt={0.25}>
      {icon && (
        <Box
          color="primary.main"
          display="flex"
          alignItems="center"
          flexShrink={0}
          sx={{ '& svg': { fontSize: featured ? 24 : 19 } }}
        >
          {icon}
        </Box>
      )}
      <Typography
        color={featured ? 'primary.main' : 'text.primary'}
        fontWeight={800}
        sx={{
          fontSize: featured ? { xs: 22, sm: 26 } : { xs: 14, sm: 16 },
          lineHeight: 1.15,
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {value || '-'}
      </Typography>
    </Box>
  </Box>
);

const InfoField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  if (!visible(value)) return null;

  return (
    <Box minWidth={0}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontSize: 12, lineHeight: 1.3, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const TrackingField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  if (!visible(value)) return null;

  return (
    <Box minWidth={0}>
      <Typography
        color="text.secondary"
        display="block"
        sx={{ fontSize: 10, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        color="text.primary"
        sx={{
          fontFamily: 'monospace',
          fontSize: 10,
          lineHeight: 1.35,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const CompactMeta = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  if (!visible(value)) return null;

  return (
    <Box minWidth={0}>
      <Typography
        color="text.secondary"
        display="block"
        sx={{ fontSize: 10, lineHeight: 1.15 }}
      >
        {label}
      </Typography>
      <Typography
        color="text.primary"
        noWrap
        sx={{ fontSize: 11, lineHeight: 1.25 }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const LinkMilestones = ({ detail, events }: { detail: any; events: any[] }) => {
  const milestones = buildMilestones(detail, events);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        display="grid"
        gridTemplateColumns={`repeat(${milestones.length}, minmax(96px, 1fr))`}
        sx={{ minWidth: { xs: 620, md: 'auto' } }}
      >
        {milestones.map((milestone, index) => {
          const done = Boolean(milestone.reachedAt);
          const active = !done && milestone.active;
          const highlighted = done || active;
          const color = highlighted ? milestone.color : 'text.disabled';
          const nextMilestone = milestones[index + 1];
          const nextHighlighted = Boolean(
            nextMilestone?.reachedAt || nextMilestone?.active
          );

          return (
            <Box
              key={milestone.key}
              textAlign="center"
              minWidth={0}
              sx={{
                px: 0.5,
                position: 'relative',
              }}
            >
              <Box height={34} position="relative" mb={0.25}>
                {index > 0 && (
                  <Box
                    position="absolute"
                    left={0}
                    right="50%"
                    top={16}
                    sx={{
                      borderTop: '2px solid',
                      borderColor: highlighted ? color : 'divider',
                      borderTopStyle: highlighted ? 'solid' : 'dashed',
                    }}
                  />
                )}
                {index < milestones.length - 1 && (
                  <Box
                    position="absolute"
                    left="50%"
                    right={0}
                    top={16}
                    sx={{
                      borderTop: '2px solid',
                      borderColor: nextHighlighted ? color : 'divider',
                      borderTopStyle: nextHighlighted ? 'solid' : 'dashed',
                    }}
                  />
                )}
                <Box
                  position="relative"
                  zIndex={1}
                  mx="auto"
                  width={34}
                  height={34}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="2px solid"
                  borderColor={color}
                  color={highlighted ? '#fff' : 'text.secondary'}
                  bgcolor={done ? color : 'background.paper'}
                  boxShadow={active ? 3 : 0}
                  sx={
                    active
                      ? {
                          '&:after': {
                            content: '""',
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            bgcolor: color,
                          },
                        }
                      : undefined
                  }
                >
                  {done ? (
                    <CheckIcon fontSize="small" />
                  ) : active ? null : (
                    <Typography variant="body2" fontWeight={700}>
                      {index + 1}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Typography
                variant="body2"
                fontWeight={800}
                noWrap
                color={highlighted ? color : 'text.secondary'}
                sx={{ fontSize: 12, lineHeight: 1.25 }}
              >
                {milestone.label}
              </Typography>
              <Typography
                variant="caption"
                color={done ? 'text.primary' : 'text.secondary'}
                display="block"
                noWrap
                sx={{ fontSize: 11, lineHeight: 1.25 }}
              >
                {done
                  ? formatDateTime(milestone.reachedAt)
                  : active
                  ? 'Atual'
                  : 'Pendente'}
              </Typography>
              {milestone.count > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                  sx={{ fontSize: 11, lineHeight: 1.2 }}
                >
                  {milestone.count} eventos
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export const LinkDetailDialog = ({
  open,
  detail,
  detailLoading,
  refreshingId,
  fullScreen,
  onClose,
  onCopy,
  onOpenCheckout,
  onRefresh,
  onSend,
  onDownloadSummary,
  onDownloadQr,
}: LinkDetailDialogProps) => {
  const theme = useTheme();
  const [actionsAnchorEl, setActionsAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const paymentLink = detail?.short_url || detail?.link_url || '';
  const qrValue = getQrValue(detail);
  const displayLinkId = getDisplayLinkId(detail);
  const recommendation = detail ? getRecommendation(detail) : null;
  const showPayer = hasAnyValue(detail, [
    'destination_type',
    'destination',
    'payer_name',
    'payer_document',
    'payer_email',
    'payer_phone',
  ]);
  const events = detail?.events || [];
  const actionsOpen = Boolean(actionsAnchorEl);
  const canSend = Boolean(
    detail && !NOT_SENDABLE_STATUSES.includes(detail.status)
  );
  const canRefresh = Boolean(detail && !FINAL_STATUSES.includes(detail.status));

  const closeActions = () => setActionsAnchorEl(null);

  const runMoreAction = (action: () => void) => {
    closeActions();
    action();
  };

  const handleRecommendationAction = () => {
    if (!detail || !recommendation) return;

    if (recommendation.action === 'send') {
      onSend(detail);
      return;
    }

    if (recommendation.action === 'refresh') {
      onRefresh(detail);
      return;
    }

    onDownloadSummary(detail);
  };

  const renderPrimaryLink = () => (
    <Box>
      <Typography
        color="text.secondary"
        display="block"
        sx={{ fontSize: 11, lineHeight: 1.15, mb: 0.4 }}
      >
        Link para pagamento
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        minWidth={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: 36,
          px: 0.75,
          bgcolor: 'background.paper',
        }}
      >
        <Box flex={1} minWidth={0}>
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {paymentLink || '-'}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.75 }} />
        <Tooltip title="Copiar">
          <span>
            <IconButton
              size="small"
              disabled={!paymentLink}
              onClick={() => paymentLink && onCopy(paymentLink)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Abrir página de pagamento">
          <span>
            <IconButton
              size="small"
              disabled={!paymentLink}
              onClick={() => paymentLink && onOpenCheckout(paymentLink)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  const renderCompactDetails = () => (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 1.1,
        height: '100%',
      }}
    >
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: qrValue ? '136px 1fr' : '1fr',
        }}
        gap={1}
        alignItems="center"
      >
        {qrValue && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={0.5}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.75,
              alignSelf: 'stretch',
              justifyContent: 'center',
            }}
          >
            <Box p={0.5} bgcolor="#fff" borderRadius={1} lineHeight={0}>
              <QRCodeCanvas id="payment-link-qr" value={qrValue} size={96} />
            </Box>
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={() => onDownloadQr(detail)}
              sx={{ minHeight: 24, py: 0, fontSize: 11 }}
            >
              QR
            </Button>
          </Box>
        )}
        <Stack spacing={1} minWidth={0}>
          {renderPrimaryLink()}
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(4, minmax(0, 1fr))',
            }}
            gap={0.75}
          >
            <CompactMeta
              label="Tipo"
              value={typeLabel[detail.type] || detail.type}
            />
            <CompactMeta
              label="Criado"
              value={detail.created_at ? formatDateTime(detail.created_at) : ''}
            />
            <CompactMeta
              label="Conferido"
              value={
                detail.last_checked_at
                  ? formatDateTime(detail.last_checked_at)
                  : ''
              }
            />
            <CompactMeta label="Código" value={detail.short_code} />
            {detail.barcode && (
              <Box gridColumn={{ xs: '1 / -1' }}>
                <CompactMeta label="Código de barras" value={detail.barcode} />
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    </Paper>
  );

  const renderPayer = () => {
    const isWhatsapp = String(detail.destination_type || '')
      .toLowerCase()
      .includes('whats');

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          p: 1.25,
          height: '100%',
        }}
      >
        <Typography variant="subtitle2" fontWeight={800} mb={1}>
          Pagador e envio
        </Typography>
        <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Box
              width={34}
              height={34}
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={isWhatsapp ? 'success.main' : 'primary.main'}
              sx={{
                bgcolor: alpha(
                  isWhatsapp
                    ? theme.palette.success.main
                    : theme.palette.primary.main,
                  0.12
                ),
              }}
            >
              {isWhatsapp ? <WhatsAppIcon /> : <SendIcon />}
            </Box>
            <Box minWidth={0}>
              <Typography variant="caption" color="text.secondary">
                Canal
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {detail.destination_type || '-'}
              </Typography>
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Box
              width={34}
              height={34}
              borderRadius="50%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="primary.main"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <PersonOutlineIcon />
            </Box>
            <Box minWidth={0}>
              <Typography variant="caption" color="text.secondary">
                Destino
              </Typography>
              <Typography variant="body2" fontWeight={600} noWrap>
                {detail.destination || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
        {hasAnyValue(detail, [
          'payer_name',
          'payer_document',
          'payer_email',
          'payer_phone',
        ]) && (
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
            gap={0.75}
            mt={1}
          >
            <InfoField label="Nome" value={detail.payer_name} />
            <InfoField label="Documento" value={detail.payer_document} />
            <InfoField label="E-mail" value={detail.payer_email} />
            <InfoField label="Telefone" value={detail.payer_phone} />
          </Box>
        )}
      </Paper>
    );
  };

  const renderPaymentSummary = () => {
    if (detail.status !== 'paid' && !detail.paid_at) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          bgcolor: alpha(theme.palette.success.main, 0.06),
          borderColor: alpha(theme.palette.success.main, 0.28),
          borderRadius: 2,
          p: 1.25,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Box
            width={32}
            height={32}
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="success.main"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.14),
            }}
          >
            <CheckIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={800}>
              Pagamento confirmado
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              Este link já possui confirmação de pagamento.
            </Typography>
          </Box>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          }}
          gap={0.85}
        >
          <CompactMeta label="Valor pago" value={formatMoney(detail.amount)} />
          <CompactMeta label="Pago em" value={formatDateTime(detail.paid_at)} />
          <CompactMeta
            label="Referência"
            value={detail.external_reference || detail.reference_id}
          />
          <CompactMeta
            label="Transação"
            value={detail.checkout_reference_id || detail.charge_reference}
          />
        </Box>
      </Paper>
    );
  };

  const renderRecommendationAction = () => {
    if (!detail || !recommendation) return null;

    if (recommendation.action === 'refresh') {
      return (
        <LoadingButton
          size="small"
          variant="contained"
          loading={refreshingId === detail.id}
          disabled={!canRefresh}
          startIcon={<RefreshIcon />}
          onClick={handleRecommendationAction}
          sx={{ alignSelf: 'flex-start' }}
        >
          {recommendation.actionLabel}
        </LoadingButton>
      );
    }

    return (
      <Button
        size="small"
        variant="contained"
        disabled={recommendation.action === 'send' && !canSend}
        startIcon={
          recommendation.action === 'send' ? <SendIcon /> : <FileDownloadIcon />
        }
        onClick={handleRecommendationAction}
        sx={{ alignSelf: 'flex-start' }}
      >
        {recommendation.actionLabel}
      </Button>
    );
  };

  const renderTracking = () => (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          minHeight: 52,
          px: 1.25,
          '& .MuiAccordionSummary-content': { my: 0.6 },
        }}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box
            width={30}
            height={30}
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="primary.main"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={800}>
              Dados técnicos do link
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11, lineHeight: 1.2 }}
            >
              Dados técnicos e referências deste link.
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, px: 1.25, pb: 1.25 }}>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
          gap={0.75}
        >
          <TrackingField label="reference_id" value={detail.reference_id} />
          <TrackingField
            label="external_reference"
            value={detail.external_reference}
          />
          <TrackingField label="short_code" value={detail.short_code} />
          <TrackingField
            label="charge_reference"
            value={detail.charge_reference}
          />
          <TrackingField
            label="checkout_reference_id"
            value={detail.checkout_reference_id}
          />
          <TrackingField label="status_raw" value={detail.status_raw} />
          <TrackingField label="status_source" value={detail.status_source} />
          <TrackingField
            label="status_original"
            value={detail.status_original}
          />
          <TrackingField
            label="status_effective"
            value={detail.status_effective}
          />
          <TrackingField label="link_id" value={detail.id} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          m: { xs: 1, sm: 2 },
          maxHeight: {
            xs: 'calc(100% - 16px)',
            sm: 'calc(100% - 32px)',
          },
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        {detail ? (
          <Box
            display="flex"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            gap={1}
            sx={{ p: { xs: 1.25, sm: 1.5 }, pb: 1 }}
          >
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <Box
                width={42}
                height={42}
                borderRadius={1.5}
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="primary.main"
                flexShrink={0}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <InsertLinkIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box minWidth={0}>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  noWrap
                  sx={{ fontSize: { xs: 19, sm: 23 }, lineHeight: 1.15 }}
                >
                  Link de pagamento
                </Typography>
                <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ minWidth: 0, fontSize: 12 }}
                  >
                    ID do link: {displayLinkId || '-'}
                  </Typography>
                  {displayLinkId && (
                    <Tooltip title="Copiar ID">
                      <IconButton
                        size="small"
                        onClick={() => onCopy(displayLinkId)}
                        sx={{ p: 0.25 }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <StatusChip status={detail.status} />
              <Tooltip title="Fechar">
                <IconButton size="small" onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2 }}
          >
            <Typography variant="h6" fontWeight={800}>
              Detalhes do link
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 1.25, sm: 1.5 }, pt: 0, pb: 1.25 }}>
        {detail && (
          <Stack spacing={1.15}>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                p: { xs: 1.15, sm: 1.25 },
              }}
            >
              <Box
                display="grid"
                gridTemplateColumns={{ xs: '1fr', md: '1fr 300px' }}
                gap={1.25}
              >
                <Box minWidth={0}>
                  <Box
                    display="grid"
                    gridTemplateColumns={{
                      xs: '1fr',
                      sm: 'repeat(3, minmax(0, 1fr))',
                    }}
                    gap={1.25}
                    mb={1}
                  >
                    <Box
                      sx={{
                        pr: { sm: 1.25 },
                        borderRight: { sm: '1px solid' },
                        borderColor: 'divider',
                      }}
                    >
                      <SummaryMetric
                        label="Valor"
                        value={formatMoney(detail.amount)}
                        featured
                      />
                    </Box>
                    <Box
                      sx={{
                        pr: { sm: 1.25 },
                        borderRight: { sm: '1px solid' },
                        borderColor: 'divider',
                      }}
                    >
                      <SummaryMetric
                        label="Vencimento"
                        value={formatDate(detail.due_date)}
                        icon={<CalendarTodayIcon />}
                      />
                    </Box>
                    <SummaryMetric
                      label="Última atividade"
                      value={formatDateTime(pickLastActivity(detail))}
                      icon={<AccessTimeIcon />}
                    />
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.75}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.16),
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      p: 0.85,
                    }}
                  >
                    <InfoOutlinedIcon color="primary" fontSize="small" />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: 12, lineHeight: 1.3 }}
                    >
                      {getStatusDescription(detail.status)}
                    </Typography>
                  </Box>
                </Box>
                {recommendation && (
                  <Paper
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.18),
                      borderRadius: 2,
                      p: 1.25,
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.primary.main,
                        0.08
                      )}, ${alpha(theme.palette.primary.light, 0.02)})`,
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <Box
                        width={36}
                        height={36}
                        borderRadius={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        color="#fff"
                        sx={{
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        }}
                      >
                        <AutoAwesomeIcon />
                      </Box>
                      <Stack spacing={1} minWidth={0}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight={800}
                            sx={{ lineHeight: 1.25 }}
                          >
                            {recommendation.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: 12, lineHeight: 1.3 }}
                          >
                            {recommendation.description}
                          </Typography>
                        </Box>
                        {renderRecommendationAction()}
                      </Stack>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Paper>

            <Box display="flex" flexWrap="wrap" gap={0.75}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!paymentLink}
                onClick={() => paymentLink && onCopy(paymentLink)}
              >
                Copiar link
              </Button>
              {canSend && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => onSend(detail)}
                >
                  Enviar
                </Button>
              )}
              {canRefresh && (
                <LoadingButton
                  size="small"
                  variant="outlined"
                  loading={refreshingId === detail.id}
                  startIcon={<RefreshIcon />}
                  onClick={() => onRefresh(detail)}
                >
                  Atualizar situação
                </LoadingButton>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<KeyboardArrowDownIcon />}
                onClick={(event) => setActionsAnchorEl(event.currentTarget)}
              >
                Mais ações
              </Button>
              <Menu
                anchorEl={actionsAnchorEl}
                open={actionsOpen}
                onClose={closeActions}
              >
                <MenuItem
                  onClick={() => runMoreAction(() => onDownloadSummary(detail))}
                >
                  <ListItemIcon>
                    <FileDownloadIcon fontSize="small" />
                  </ListItemIcon>
                  Baixar resumo
                </MenuItem>
                <MenuItem
                  disabled={!qrValue}
                  onClick={() => runMoreAction(() => onDownloadQr(detail))}
                >
                  <ListItemIcon>
                    <FileDownloadIcon fontSize="small" />
                  </ListItemIcon>
                  Baixar QR
                </MenuItem>
                <MenuItem
                  disabled={!paymentLink}
                  onClick={() =>
                    runMoreAction(() => onOpenCheckout(paymentLink))
                  }
                >
                  <ListItemIcon>
                    <OpenInNewIcon fontSize="small" />
                  </ListItemIcon>
                  Abrir pagamento
                </MenuItem>
              </Menu>
            </Box>

            <Box
              display="grid"
              gridTemplateColumns={{
                xs: '1fr',
                md: showPayer ? '1.35fr 0.85fr' : '1fr',
              }}
              gap={1}
            >
              {renderCompactDetails()}
              {showPayer && renderPayer()}
            </Box>

            {renderPaymentSummary()}

            <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.25 }}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                Histórico
              </Typography>
              {detailLoading && (
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(6, minmax(0, 1fr))',
                  }}
                  gap={1}
                >
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                </Box>
              )}
              {!detailLoading && (
                <LinkMilestones detail={detail} events={events} />
              )}
              {!detailLoading && !events.length && !detail.created_at && (
                <Typography variant="body2" color="text.secondary">
                  Sem eventos registrados.
                </Typography>
              )}
            </Paper>

            {renderTracking()}

            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={0.75}
              color="text.disabled"
            >
              <LockOutlinedIcon sx={{ fontSize: 15 }} />
              <Typography sx={{ fontSize: 11 }}>
                As informações deste link são confidenciais e de uso restrito.
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LinkDetailDialog;
